import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Shield } from "lucide-react";
import { services, serviceBySlug } from "../data/services";
import { citiesByProvince } from "../data/cities";
import {
  FR_SERVICE_SLUGS,
  FR_SLUG_TO_EN,
  FR_SLUGS,
  frServiceNames,
  frServiceShorts,
  frServiceDeliverables,
  frServiceFaqs,
  frServiceParas,
  frCityName,
  frLabels,
} from "../data/frContent";
import { getHead } from "../seo/routes";
import { Seo } from "../components/Seo";
import { PageHero, Prose, FAQBlock, CTABand, Breadcrumbs, LinkGrid, SectionHead } from "../components/Blocks";
import { IconTile, SERVICE_ICONS } from "../components/Icons";
import NotFound from "./NotFound";

const qcCities = citiesByProvince("quebec");

export function FrServicesIndex() {
  const head = getHead("/fr/services");
  return (
    <>
      <Seo title={head.title} description={head.description} path="/fr/services" lang="fr-CA" alternates={head.alternates} />
      <PageHero
        overline="Gamme complète"
        title="Services de sécurité professionnels"
        lead="Vingt services de sécurité spécialisés, offerts par du personnel licencié avec rapports vérifiés — dans toutes les villes du Québec et partout au Canada."
      />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="fr-services-directory">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s) => (
            <Link
              key={s.slug}
              to={`/fr/services/${FR_SERVICE_SLUGS[s.slug]}`}
              data-testid={`fr-service-card-${s.slug}`}
              className="group border border-white/10 bg-[#181C28] rounded-sm overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:border-[#FF5500]/50 hover:shadow-[0_10px_30px_rgba(255,85,0,0.15)]"
            >
              <IconTile icon={SERVICE_ICONS[s.slug] || Shield} className="h-36 border-b border-white/10" />
              <div className="p-5">
                <h2 className="font-display text-lg font-bold uppercase tracking-tight text-white group-hover:text-[#FF5500] transition-colors">{frServiceNames[s.slug]}</h2>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{frServiceShorts[s.slug]}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-[#FF5500]">
                  Voir le service <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <CTABand title="Quel service vous convient?" sub="Décrivez votre propriété et vos risques à notre équipe de répartition — nous vous recommanderons la bonne couverture, sans détour." />
    </>
  );
}

export function FrServicePage() {
  const { slug } = useParams();
  const service = serviceBySlug[FR_SLUG_TO_EN[slug || ""]];
  if (!service) return <NotFound />;
  const path = `/fr/services/${slug}`;
  const head = getHead(path);
  const sName = frServiceNames[service.slug];
  const faqs = frServiceFaqs(service);
  const related = services.filter((s) => s.slug !== service.slug).slice(0, 8);
  return (
    <>
      <Seo title={head.title} description={head.description} path={path} lang="fr-CA" alternates={head.alternates} />
      <Breadcrumbs items={[{ label: frLabels.breadcrumbHome, to: "/" }, { label: "Services", to: "/fr/services" }, { label: sName }]} />
      <PageHero overline="Service de sécurité" title={sName} lead={frServiceShorts[service.slug]} image={service.image} />
      <Prose paragraphs={frServiceParas(service)} />
      <section className="bg-[#131620] border-y border-white/10" data-testid="fr-service-deliverables">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <SectionHead overline={frLabels.scope} title={`Ce que comprend le service`} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {frServiceDeliverables[service.slug].map((d) => (
              <div key={d} className="flex items-start gap-3 border border-white/10 bg-[#0D0E12] p-4 rounded-sm">
                <CheckCircle2 className="h-5 w-5 text-[#FF5500] shrink-0 mt-0.5" />
                <span className="text-sm text-gray-300 leading-relaxed">{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <LinkGrid
        overline="Couverture au Québec"
        title={`${sName} offert dans 99 villes du Québec`}
        items={qcCities.map((c) => ({
          to: `/fr/${FR_SERVICE_SLUGS[service.slug]}-a-${c.slug}`,
          label: frCityName(c.slug),
          testid: `fr-service-city-link-${c.slug}`,
        }))}
      />
      <FAQBlock faqs={faqs} heading={`${frLabels.faqHeading} — ${sName}`} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="fr-related-services">
        <SectionHead overline="Explorer" title="Autres services de sécurité" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {related.map((s) => (
            <Link key={s.slug} to={`/fr/services/${FR_SERVICE_SLUGS[s.slug]}`} data-testid={`fr-related-service-${s.slug}`} className="group flex items-center justify-between border border-white/10 bg-[#181C28] hover:border-[#FF5500]/50 rounded-sm px-4 py-3 transition-all duration-200">
              <span className="text-sm text-gray-300 group-hover:text-white">{frServiceNames[s.slug]}</span>
              <ArrowRight className="h-3.5 w-3.5 text-[#FF5500]" />
            </Link>
          ))}
        </div>
      </section>
      <CTABand title={`${sName} : parlons de votre site`} sub="Appelez notre répartition 24/7 ou écrivez-nous. Une visite des lieux et des consignes écrites précèdent toujours le premier quart." />
    </>
  );
}
